import React, { useState } from 'react';
import { Check } from 'lucide-react';
import gafferIcon from '../assets/gaffer-head-icon.png';

/**
 * The Beat The Gaffer opt-in, shared by the create-game wizard, the league settings
 * and the game page.
 *
 * Three rules for the copy here, and they apply everywhere the Gaffer is mentioned:
 *  - never call him a bot. He is the Big Dog, a rival to be beaten, not a feature.
 *  - never explain how he picks. Working that out is half the game — spelling it out
 *    hands every player a counter and leaves nothing to beat.
 *  - do not sell him either. One line is the whole pitch; people get it. Anything
 *    added here reads as an instruction manual for a character who should not have one.
 *
 * The prize warning is the one thing that must always be said when the toggle is on:
 * the Gaffer is a real competitor who can win, and a manager who has not thought that
 * through is the one person who needs telling.
 *
 * **It is a card, not a checkbox in a form group.** It used to be one, and inherited
 * `.form-group input` — a rule written for a text field, so the box rendered full
 * width with a tick floating in the middle of it and the words underneath. That is
 * fixed at the root too, but this control should never have been a tick box in a
 * column of fields: the choice is whether to let a named rival into your game, the
 * one decision in the wizard with a face attached, and it now carries his. The whole
 * card is the hit target, and the state is carried by the border and the tick rather
 * than by a native box that no amount of CSS makes look like this app.
 *
 * **The box follows the finger, not the server.** In the wizards `checked` is local
 * state and flips instantly; on the league page it is a saved setting behind a PUT and
 * a full reload, and rendering straight from that left a ticked box unticked for the
 * length of the round trip. It looked like the tap had missed.
 *
 * So a tap is held here until the owner catches up. The hold is dropped the moment
 * `checked` actually changes — a save that landed, a rollback, a change made in another
 * tab — which is what stops this copy outliving the truth. Written as a render-time
 * comparison rather than an effect on `checked` for a reason: an effect belonging to
 * the render that first showed the box can flush *after* the first tap, and it would
 * then reset the box the player had just ticked.
 */
function GafferToggle({ checked, onChange, disabled = false, disabledReason = '', note = '' }) {
  const settled = Boolean(checked);
  // null means "no unsaved tap" - follow the owner.
  const [tapped, setTapped] = useState(null);
  const [lastSettled, setLastSettled] = useState(settled);

  if (lastSettled !== settled) {
    setLastSettled(settled);
    setTapped(null);
  }

  const shown = tapped === null ? settled : tapped;

  const handle = (next) => {
    setTapped(next);
    // A caller that reports failure by throwing gets the box put straight back. One
    // that swallows its own errors is covered by `checked` never having moved: the
    // hold stands until something changes it, which is the honest state of a setting
    // whose save did not land.
    Promise.resolve(onChange(next)).catch(() => setTapped(null));
  };

  const cardClass = [
    'gaffer-card',
    shown ? 'is-on' : '',
    disabled ? 'is-disabled' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className="gaffer-toggle">
      <label className={cardClass}>
        {/* Visually hidden rather than replaced: it stays the real control, so the
            label, the keyboard and every assistive technology work untouched. */}
        <input
          type="checkbox"
          className="gaffer-card-input"
          checked={shown}
          onChange={(e) => handle(e.target.checked)}
          disabled={disabled}
          aria-label="Beat The Gaffer"
        />

        <span className="gaffer-card-face">
          <img src={gafferIcon} alt="" aria-hidden="true" />
        </span>

        <span className="gaffer-card-text">
          <span className="gaffer-card-title">Beat The Gaffer</span>
          <span className="gaffer-card-blurb">
            Are you brave enough to take on the Big Dog in your games?
          </span>
        </span>

        <span className="gaffer-card-tick" aria-hidden="true">
          <Check size={15} strokeWidth={3} />
        </span>
      </label>

      {shown && (
        <p className="helper-text warning-text" role="note">
          Heads up: the Gaffer can win. If you're playing for a pot, think twice about
          letting him in — someone has to explain where the money went.
        </p>
      )}

      {/* What this setting does not reach. Only the league page has anything to say
          here: a game already running keeps whatever it started with. */}
      {note && <p className="helper-text gaffer-card-note">{note}</p>}

      {disabled && disabledReason && (
        <p className="helper-text gaffer-card-note">{disabledReason}</p>
      )}
    </div>
  );
}

export default GafferToggle;
